import React, { useCallback, useEffect, useRef, useState } from "react";
import { api as axios } from "../lib/axios";
import { toast } from "sonner";

import Topbar from "../components/Topbar";
import UploadZone from "../components/UploadZone";
import VideoStage from "../components/VideoStage";
import Sidebar from "../components/Sidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Studio() {
  const [file, setFile] = useState(null);
  const [roi, setRoi] = useState(null); // { x,y,w,h } absolute
  const [band, setBand] = useState(1);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);

  // Stats (just count of today)
  const [processedToday, setProcessedToday] = useState(0);

  // Batch state
  const [batchFiles, setBatchFiles] = useState([]); // [{id, file, status, progress, token, elapsed_sec, error}]
  const [batchRunning, setBatchRunning] = useState(false);
  const batchAbortRef = useRef(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/templates`);
      setTemplates(res.data.items || []);
    } catch (e) {
      console.error("templates fetch failed", e);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      const today = new Date().toISOString().slice(0, 10);
      const day = (res.data.days || []).find((d) => d._id === today);
      setProcessedToday(day?.processed || 0);
    } catch (_) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchStats();
  }, [fetchTemplates, fetchStats]);

  const handleFile = (f) => {
    setFile(f);
    setRoi(null);
    setResult(null);
  };

  const handleReset = () => {
    setFile(null);
    setRoi(null);
    setResult(null);
    setProgress(0);
  };

  const handleProcess = async () => {
    if (!file || !roi) return;
    setBusy(true);
    setProgress(0);
    setResult(null);

    const form = new FormData();
    form.append("file", file);
    form.append("roi", JSON.stringify(roi));
    form.append("band", String(band));

    try {
      const res = await axios.post(`${API}/process`, form, {
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 90);
            setProgress(pct);
          }
        },
        timeout: 0,
      });
      setProgress(100);
      setResult(res.data);
      toast.success("Procesamiento completado", {
        description: `${res.data.elapsed_sec}s · ROI ${res.data.roi_applied.w}×${res.data.roi_applied.h} · band ${res.data.roi_applied.band}`,
      });
      fetchStats();
    } catch (err) {
      const detail = err?.response?.data?.error || err.message;
      toast.error("Error al procesar", { description: detail });
      setProgress(0);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.token) return;
    try {
      const res = await axios.get(`${API}/download/${result.token}`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `delogo_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Descarga iniciada", {
        description: "El archivo fue eliminado del servidor.",
      });
      setResult(null);
    } catch (e) {
      toast.error("No se pudo descargar", {
        description: e?.response?.data?.error || e.message,
      });
    }
  };

  // -------- Templates --------
  const handleSaveTemplate = async () => {
    if (!roi || !templateName.trim()) return;
    setSavingTpl(true);
    try {
      const res = await axios.post(`${API}/templates`, {
        name: templateName.trim(),
        roi,
        band,
      });
      setTemplates((prev) => [res.data, ...prev]);
      setTemplateName("");
      toast.success("Plantilla guardada", { description: res.data.name });
    } catch (e) {
      toast.error("No se pudo guardar la plantilla", {
        description: e?.response?.data?.error || e.message,
      });
    } finally {
      setSavingTpl(false);
    }
  };

  const handleApplyTemplate = (tpl) => {
    setRoi({ ...tpl.roi });
    setBand(tpl.band ?? 1);
    toast.info("Plantilla aplicada", {
      description: `${tpl.name} · ${tpl.roi.w}×${tpl.roi.h} · band ${tpl.band}`,
    });
  };

  const handleDeleteTemplate = async (tpl) => {
    try {
      await axios.delete(`${API}/templates/${tpl._id}`);
      setTemplates((prev) => prev.filter((t) => t._id !== tpl._id));
      toast.success("Plantilla eliminada", { description: tpl.name });
    } catch (e) {
      toast.error("No se pudo eliminar", {
        description: e?.response?.data?.error || e.message,
      });
    }
  };

  // -------- Batch processing --------
  const handleBatchPickFiles = (files) => {
    const videos = files.filter((f) => f.type.startsWith("video/"));
    if (videos.length === 0) {
      toast.error("Selecciona archivos de video");
      return;
    }
    const skipped = files.length - videos.length;
    setBatchFiles((prev) => [
      ...prev,
      ...videos.map((f) => ({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        file: f,
        status: "pending",
        progress: 0,
        token: null,
        elapsed_sec: null,
        error: null,
      })),
    ]);
    toast.success(`${videos.length} archivo(s) añadido(s)`, {
      description: skipped ? `${skipped} omitidos (no video)` : undefined,
    });
  };

  const updateBatchItem = (id, patch) => {
    setBatchFiles((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    );
  };

  const processBatchItem = async (item) => {
    if (!roi) throw new Error("Dibuja una ROI antes de iniciar el lote");
    updateBatchItem(item.id, { status: "uploading", progress: 0, error: null });
    const form = new FormData();
    form.append("file", item.file);
    form.append("roi", JSON.stringify(roi));
    form.append("band", String(band));

    try {
      const res = await axios.post(`${API}/process`, form, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 90);
            updateBatchItem(item.id, { progress: pct, status: "uploading" });
          }
        },
      });
      updateBatchItem(item.id, {
        status: "done",
        progress: 100,
        token: res.data.token,
        elapsed_sec: res.data.elapsed_sec,
      });
    } catch (err) {
      const detail = err?.response?.data?.error || err.message;
      updateBatchItem(item.id, {
        status: "error",
        progress: 0,
        error: detail,
      });
    }
  };

  const handleBatchStart = async () => {
    if (!roi) {
      toast.error("Dibuja una ROI primero", {
        description: "El lote usa la ROI y feather actuales.",
      });
      return;
    }
    const pending = batchFiles.filter((f) => f.status === "pending");
    if (pending.length === 0) {
      toast.info("Nada que procesar");
      return;
    }
    setBatchRunning(true);
    batchAbortRef.current = false;

    for (const item of pending) {
      if (batchAbortRef.current) break;
      // Re-read current state to allow stop signal
      await processBatchItem(item);
    }
    setBatchRunning(false);
    fetchStats();

    if (batchAbortRef.current) {
      toast.info("Lote detenido");
    } else {
      // Count using current state via setBatchFiles callback-free read
      setBatchFiles((curr) => {
        const done = curr.filter((f) => f.status === "done").length;
        const err = curr.filter((f) => f.status === "error").length;
        toast.success("Lote completado", {
          description: `${done} OK${err ? ` · ${err} con error` : ""}`,
        });
        return curr;
      });
    }
  };

  const handleBatchStop = () => {
    batchAbortRef.current = true;
  };

  const handleBatchClear = () => {
    setBatchFiles([]);
  };

  const handleBatchRemoveItem = (id) => {
    setBatchFiles((prev) => prev.filter((it) => it.id !== id));
  };

  const downloadToken = async (token, filename) => {
    const res = await axios.get(`${API}/download/${token}`, {
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const handleBatchDownloadItem = async (item) => {
    if (!item.token) return;
    try {
      const cleanName = item.file.name.replace(/\.[^/.]+$/, "");
      await downloadToken(item.token, `${cleanName}_delogo.mp4`);
      // After download, server deletes; mark item as downloaded by clearing token
      updateBatchItem(item.id, { token: null });
      toast.success("Descargado", { description: item.file.name });
    } catch (e) {
      toast.error("No se pudo descargar", {
        description: e?.response?.data?.error || e.message,
      });
    }
  };

  const handleBatchDownloadAll = async () => {
    const items = batchFiles.filter((f) => f.status === "done" && f.token);
    for (const it of items) {
      try {
        const cleanName = it.file.name.replace(/\.[^/.]+$/, "");
        await downloadToken(it.token, `${cleanName}_delogo.mp4`);
        updateBatchItem(it.id, { token: null });
        // Small delay to avoid browser blocking multiple downloads
        await new Promise((r) => setTimeout(r, 300));
      } catch (e) {
        toast.error(`Falló: ${it.file.name}`);
      }
    }
    toast.success(`Descargados ${items.length} archivo(s)`);
  };

  return (
    <div
      className="h-screen flex flex-col overflow-hidden bg-[#0A0A0A] text-[#FDFDFD] relative z-[2]"
      data-testid="studio-root"
    >
      <Topbar processedToday={processedToday} />

      {!file ? (
        <main className="flex-1 flex items-center justify-center overflow-y-auto">
          <UploadZone onFile={handleFile} />
        </main>
      ) : (
        <main className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 min-h-0">
          <section
            className="lg:col-span-9 bg-[#141414] border border-[#27272A] rounded-md relative flex flex-col overflow-hidden"
            data-testid="player-area"
          >
            <VideoStage
              file={file}
              roi={roi}
              onRoiChange={setRoi}
              onReset={handleReset}
            />
          </section>

          <Sidebar
            file={file}
            roi={roi}
            band={band}
            onBandChange={setBand}
            templates={templates}
            templateName={templateName}
            onTemplateNameChange={setTemplateName}
            onSaveTemplate={handleSaveTemplate}
            onApplyTemplate={handleApplyTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            savingTpl={savingTpl}
            busy={busy}
            progress={progress}
            result={result}
            onProcess={handleProcess}
            onDownload={handleDownload}
            batchFiles={batchFiles}
            batchRunning={batchRunning}
            onBatchPickFiles={handleBatchPickFiles}
            onBatchStart={handleBatchStart}
            onBatchStop={handleBatchStop}
            onBatchClear={handleBatchClear}
            onBatchRemoveItem={handleBatchRemoveItem}
            onBatchDownloadItem={handleBatchDownloadItem}
            onBatchDownloadAll={handleBatchDownloadAll}
          />
        </main>
      )}

      <footer
        className="border-t border-[#27272A] px-6 py-2 text-[10px] uppercase tracking-[0.2em] text-[#71717A] flex items-center justify-between"
        data-testid="app-footer"
      >
        <span>
          FFmpeg · <span className="text-[#A1A1AA]">delogo filter</span> · ©
          DelogoStudio
        </span>
        <span className="hidden sm:inline">
          Modo single-user · Archivos purgados cada 12h · Sin tracking
        </span>
      </footer>
    </div>
  );
}
